/**
 * OV2-only QueryClient singleton — shared across list + detail routes.
 * V1 never imports this module.
 */
import { QueryClient } from "@tanstack/react-query";
import { mv2RetryDelay, shouldRetryMv2Query } from "@/features/_mv2/makeQueryClient";
import {
  ORDERS_QUERY_KEY,
  buildOrdersQueryParams,
  fetchOrdersFromApi,
} from "./ordersQueryApi";
import { ORDER_CATALOG_KEY, fetchOrderCatalog } from "./hooks/useOrderCatalog";
import { setupMv2CachePersist } from "@/features/_mv2/mv2CachePersist";
import { prefetchDefaultNewOrderPrice } from "./lib/newOrderPriceCache";

export const ordersQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      gcTime: 30 * 60 * 1000,
      retry: shouldRetryMv2Query,
      retryDelay: mv2RetryDelay,
      refetchOnWindowFocus: true,
    },
  },
});

setupMv2CachePersist(ordersQueryClient, "orders");

/** Warm New Order catalog (9 GETs) — 30 min staleTime matches useOrderCatalog. */
export function prefetchOrderCatalog() {
  return ordersQueryClient
    .prefetchQuery({
      queryKey: [ORDER_CATALOG_KEY],
      queryFn: fetchOrderCatalog,
      staleTime: 30 * 60 * 1000,
    })
    .then(() => prefetchDefaultNewOrderPrice());
}

/** Warm Today tab list while user hovers Orders nav. */
export function prefetchOrdersToday(upcomingPageSize = 6) {
  const params = buildOrdersQueryParams({
    activeTab: "today",
    page: 1,
    filters: { search: "", start_date: "", end_date: "" },
    upcomingPageSize,
    priorityOnly: false,
  });
  return ordersQueryClient.prefetchQuery({
    queryKey: [ORDERS_QUERY_KEY, params],
    queryFn: ({ signal }) => fetchOrdersFromApi(params, signal),
  });
}

/** Find a list-row order in any cached orders query (for instant detail seed). */
export function findOrderInListCache(orderId) {
  const needle = String(orderId);
  const entries = ordersQueryClient.getQueriesData({ queryKey: [ORDERS_QUERY_KEY] });
  for (const [, data] of entries) {
    const orders = data?.orders;
    if (!Array.isArray(orders)) continue;
    const found = orders.find((o) => String(o.id) === needle);
    if (found) return found;
  }
  return null;
}

export function ordersListPath(tab = "today") {
  return `/orders?tab=${encodeURIComponent(tab)}`;
}
